/*
 * SEARCHABLE SELECT
 *
 * Controlled single-select dropdown with local option search.
 *
 * Implementation notes:
 * - Uses a button trigger, focused combobox search input, and one listbox.
 * - Supports mouse, outside-click, Arrow key, Enter, and Escape interactions.
 * - Keeps only transient query and active-option state internally; selection stays controlled.
 * - Matches the native input 16px typography through 768px, retaining compact text above mobile widths.
 */

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type KeyboardEvent,
} from 'react';
import { foundationRadiusTokens, foundationSemantic } from '../../foundation';
import { cx } from './utils';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

export interface SearchableSelectProps {
  id: string;
  label: string;
  value: string;
  options: SearchableSelectOption[];
  onChange: (value: string) => void;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
}

const SearchableSelect = ({
  id,
  label,
  value,
  options,
  onChange,
  searchPlaceholder = 'Search options...',
  emptyMessage = 'No options found.',
  className,
  disabled = false,
}: SearchableSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listboxId = `${id}-listbox`;
  const selectedOption = options.find((option) => option.value === value);
  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    if (!normalizedQuery) {
      return options;
    }

    return options.filter((option) => option.label.toLocaleLowerCase().includes(normalizedQuery));
  }, [options, query]);

  const closeDropdown = (restoreTriggerFocus: boolean) => {
    setIsOpen(false);
    setQuery('');
    setActiveIndex(-1);
    if (restoreTriggerFocus) {
      triggerRef.current?.focus();
    }
  };

  const openDropdown = () => {
    if (disabled) {
      return;
    }

    const selectedIndex = options.findIndex((option) => option.value === value);
    setQuery('');
    setActiveIndex(options.length ? Math.max(0, selectedIndex) : -1);
    setIsOpen(true);
  };

  const handleSelect = (option: SearchableSelectOption) => {
    onChange(option.value);
    closeDropdown(true);
  };

  const handleTriggerKeyDown: ButtonHTMLAttributes<HTMLButtonElement>['onKeyDown'] = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      openDropdown();
    }
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((currentIndex) => Math.min(currentIndex + 1, filteredOptions.length - 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((currentIndex) => (
        currentIndex <= 0 ? Math.max(filteredOptions.length - 1, 0) : currentIndex - 1
      ));
      return;
    }

    if (event.key === 'Enter' && activeIndex >= 0 && filteredOptions[activeIndex]) {
      event.preventDefault();
      handleSelect(filteredOptions[activeIndex]);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDropdown(true);
    }
  };

  useEffect(() => {
    if (isOpen) {
      searchInputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        closeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  return (
    <div ref={containerRef} className={cx('relative min-w-0', className)}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        aria-label={selectedOption?.label ?? label}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        disabled={disabled}
        onClick={() => {
          if (isOpen) {
            closeDropdown(false);
          } else {
            openDropdown();
          }
        }}
        onKeyDown={handleTriggerKeyDown}
        className={cx(
          'flex h-9 w-full min-w-0 items-center justify-between gap-2 border px-3 py-1.5 text-left text-sm [@media(max-width:768px)]:text-base [@media(max-width:768px)]:leading-5',
          foundationSemantic.input.base,
          foundationSemantic.input.surface,
          foundationSemantic.input.border,
          foundationSemantic.input.text,
        )}
      >
        <span className="min-w-0 flex-1 truncate">{selectedOption?.label ?? value}</span>
        <svg
          className={cx('h-4 w-4 shrink-0 transition-transform', isOpen && 'rotate-180')}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.1 1.02l-4.25 4.5a.75.75 0 01-1.1 0l-4.25-4.5a.75.75 0 01.02-1.04z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          className={cx(
            'absolute z-50 mt-1 w-full min-w-[12rem] border p-1',
            foundationRadiusTokens.md,
            foundationSemantic.menu.container,
          )}
        >
          <input
            ref={searchInputRef}
            type="text"
            role="combobox"
            aria-label={`Search ${label.toLocaleLowerCase()}`}
            aria-autocomplete="list"
            aria-expanded="true"
            aria-controls={listboxId}
            aria-activedescendant={
              activeIndex >= 0 && filteredOptions[activeIndex]
                ? `${id}-option-${activeIndex}`
                : undefined
            }
            value={query}
            onChange={(event) => {
              const nextQuery = event.target.value;
              setQuery(nextQuery);
              const normalizedQuery = nextQuery.trim().toLocaleLowerCase();
              setActiveIndex(options.some((option) => (
                !normalizedQuery || option.label.toLocaleLowerCase().includes(normalizedQuery)
              )) ? 0 : -1);
            }}
            onKeyDown={handleSearchKeyDown}
            placeholder={searchPlaceholder}
            className={cx(
              'h-9 w-full border px-3 py-1.5 text-sm',
              foundationSemantic.input.base,
              foundationSemantic.input.surface,
              foundationSemantic.input.border,
              foundationSemantic.input.text,
              foundationSemantic.input.placeholder,
            )}
          />

          <div id={listboxId} role="listbox" aria-label={`${label} options`} className="mt-1 max-h-60 overflow-y-auto">
            {filteredOptions.map((option, optionIndex) => {
              const isSelected = option.value === value;
              const isActive = optionIndex === activeIndex;

              return (
                <button
                  key={option.value}
                  id={`${id}-option-${optionIndex}`}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setActiveIndex(optionIndex)}
                  onClick={() => handleSelect(option)}
                  className={cx(
                    'w-full px-3 py-2 text-left text-sm transition-colors',
                    foundationRadiusTokens.sm,
                    foundationSemantic.menu.itemDefault,
                    foundationSemantic.interactive.hoverSurface,
                    foundationSemantic.interactive.focusRing,
                    isSelected && foundationSemantic.menu.itemSelected,
                    isActive && 'bg-surface-muted',
                  )}
                >
                  <span className="block truncate">{option.label}</span>
                </button>
              );
            })}

            {!filteredOptions.length && (
              <div className={cx('px-3 py-3 text-sm', foundationSemantic.text.secondary)}>
                {emptyMessage}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
